import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisBottom } from '@visx/axis';
import { scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import _ from 'lodash';

import { getParams, thousandFormat } from '../../../../Utils/Functions';
import { formatedStyle } from '../../../../Assets/js/constant';

let tooltipTimeout;

function getWorkingDaysInMonth(selectedDimDate) {
  if (!selectedDimDate) return 0;

  const digits = String(selectedDimDate).replace(/\D/g, '');
  if (digits.length < 6) return 0;

  const year = parseInt(digits.slice(0, 4), 10);
  const month = parseInt(digits.slice(4, 6), 10);

  if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12) {
    return 0;
  }

  let count = 0;
  const daysInMonth = new Date(year, month, 0).getDate();

  for (let dayOfMonth = 1; dayOfMonth <= daysInMonth; dayOfMonth += 1) {
    const day = new Date(year, month - 1, dayOfMonth).getDay();
    if (day !== 0 && day !== 6) count += 1;
  }

  return count;
}

const BottomChart = ({
  detailData,
  selectedDimDate,
}) => {
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [selectedRightBars, setSelectedRightBars] = useState([]);
  const [selectedLeftItems, setSelectedLeftItems] = useState([]);
  const [selectedRightItems, setSelectedRightItems] = useState([]);
  const [avgHours, setAvgHours] = useState([]);
  const [availableTotal, setAvailableTotal] = useState(0);
  const [hoursTotal, setHoursTotal] = useState(0);
  const [rightdata, setRightdata] = useState([]);
  const [groupValidation, setGroupValidation] = useState(true);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  useEffect(() => {
    prepareData();
  }, [selectedLeftItems, detailData]);

  const prepareData = useCallback(() => {
    let rightdata = [],
      avTotal = 0,
      hTotal = 0,
      avgHours = [];

    let rightColors = ['#1c75bc', '#88cfee'];
    let rightDisableColors = ['#ccdce9', '#dfedf2'];
    if (detailData === undefined) return;

    let group_validation = false;
    // getting Right barchart data
    if (detailData['Right'] !== undefined) {
      detailData['Right'].forEach((item) => {
        if (item.Group && item.Group !== '' && item.Group !== null) {
          group_validation = true;
          return;
        }
      });
      
      if (group_validation) {
        detailData['Right'].sort(
          (a, b) =>
            a.EmployeeRole.localeCompare(b.EmployeeRole) ||
            a.EmployeeName.localeCompare(b.EmployeeName),
        );
        let right_group_data = _.mapValues(
          _.groupBy(detailData['Right'], 'Group'),
          (groupData) => groupData.map((data) => _.omit(data, 'Group')),
        );

        let groupFieldExisting = false;
        detailData['Right'].forEach((item) => {
          selectedLeftItems.forEach((leitem) => {
            if (
              (leitem.Group === item.Group && groupFieldExisting === false) ||
              leitem.month === -1
            ) {
              groupFieldExisting = true;
            }
          });
        });

        if (selectedLeftItems.length === 0) groupFieldExisting = true;

        if (groupFieldExisting) {
          Object.keys(right_group_data).forEach((gKey) => {
            let roleFieldExisting = false;
            detailData['Right'].forEach((item) => {
              selectedLeftItems.forEach((leitem) => {
                if (
                  (leitem.EmployeeRole === item.EmployeeRole &&
                    roleFieldExisting === false) ||
                  leitem.month === -1
                ) {
                  roleFieldExisting = true;
                }
              });
            });

            if (selectedLeftItems.length === 0) roleFieldExisting = true;

            if (roleFieldExisting) {
              let right_role_data = _.mapValues(
                _.groupBy(right_group_data[gKey], 'EmployeeRole'),
                (roleData) =>
                  roleData.map((data) => _.omit(data, 'EmployeeRole')),
              );

              let groupRightData = {
                Group: gKey,
                subItems: [],
              };

              Object.keys(right_role_data).forEach((rKey) => {
                let roleRightData = {
                  EmployeeRole: rKey,
                  ActualFteEquivalentMTD: 0,
                  subItems: [],
                };

                let findRoleData = detailData['Left'].find(
                  (d) => d.EmployeeRole === rKey && d.Group === gKey,
                );
                if (findRoleData) {
                  roleRightData.ActualFteEquivalentMTD = findRoleData.ActualFteEquivalentMTD;
                  avTotal += roleRightData.ActualFteEquivalentMTD;
                }

                let right_name_data = _.mapValues(
                  _.groupBy(right_role_data[rKey], 'EmployeeName'),
                  (nameData) =>
                    nameData.map((data) => _.omit(data, 'EmployeeName')),
                );

                Object.keys(right_name_data).forEach((nKey) => {
                  let nameData = {
                    EmployeeRole: rKey,
                    Color: rightColors,
                    DisableColor: rightDisableColors,
                    value: {},
                  };
                  right_name_data[nKey].forEach((item) => {
                    avgHours.push(parseFloat(item.AverageHoursWorkedPerDay));
                    if (nameData.value[nKey] === undefined) {
                      nameData.value[nKey] = {};
                      nameData.value[nKey].EmployeeName = nKey;
                      nameData.value[nKey].TargetUtilisationRation = Number(
                        item.TargetUtilisationRation,
                      );
                      nameData.value[nKey].ChargeableHoursWorkedMTD = Number(
                        item.ChargeableHoursWorkedMTD,
                      );
                      nameData.value[nKey].HoursWorked = item.HoursWorked
                        ? Math.round(item.HoursWorked)
                        : 0;
                      hTotal += nameData.value[nKey].HoursWorked;
                      nameData.value[nKey].NonChargeableHoursWorkedMTD = Number(
                        item.NonChargeableHoursWorkedMTD,
                      );
                    } else {
                      // Target % is the same each month; do not add when aggregating months
                      nameData.value[nKey].TargetUtilisationRation = Number(
                        item.TargetUtilisationRation,
                      );
                      nameData.value[nKey].ChargeableHoursWorkedMTD += Number(
                        item.ChargeableHoursWorkedMTD,
                      );
                      const hoursToAdd = item.HoursWorked
                        ? Math.round(item.HoursWorked)
                        : 0;
                      nameData.value[nKey].HoursWorked += hoursToAdd;
                      hTotal += hoursToAdd;
                      nameData.value[nKey].NonChargeableHoursWorkedMTD +=
                        Number(item.NonChargeableHoursWorkedMTD);
                    }
                  });
                  roleRightData.subItems.push(nameData);
                });
                groupRightData.subItems.push(roleRightData);
              });
              rightdata.push(groupRightData);
            }
          });
        }
      } else {
        let roleRightData = {};
        detailData['Right'].sort(
          (a, b) =>
            a.EmployeeRole.localeCompare(b.EmployeeRole) ||
            a.EmployeeName.localeCompare(b.EmployeeName),
        );

        detailData['Right'].forEach((item) => {
          let findRoleData = detailData['Left'].find(
            (d) => d.EmployeeRole === item.EmployeeRole,
          );
          let roleFieldExisting = false;
          selectedLeftItems.forEach((leitem) => {
            if (
              (leitem.EmployeeRole === item.EmployeeRole &&
                roleFieldExisting === false) ||
              leitem.month === -1
            ) {
              roleFieldExisting = true;
            }
          });
          if (selectedLeftItems.length === 0) roleFieldExisting = true;

          if (roleFieldExisting) {
            if (roleRightData[item.EmployeeRole] === undefined) {
              roleRightData[item.EmployeeRole] = {};
              roleRightData[item.EmployeeRole]['EmployeeRole'] =
                item.EmployeeRole;
              roleRightData[item.EmployeeRole]['ActualFteEquivalentMTD'] =
                findRoleData
                  ? findRoleData.ActualFteEquivalentMTD
                  : 0;
              avTotal +=
                roleRightData[item.EmployeeRole]['ActualFteEquivalentMTD'];
              roleRightData[item.EmployeeRole]['Color'] = rightColors;
              roleRightData[item.EmployeeRole]['DisableColor'] =
                rightDisableColors;
              roleRightData[item.EmployeeRole]['value'] = {};
            }
            if (
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName] ===
              undefined
            ) {
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName] = {};
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                'EmployeeName'
              ] = item.EmployeeName;
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                'ChargeableHoursWorkedMTD'
              ] = parseFloat(item.ChargeableHoursWorkedMTD);
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                'NonChargeableHoursWorkedMTD'
              ] = parseFloat(item.NonChargeableHoursWorkedMTD);
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                'HoursWorked'
              ] = item.HoursWorked
                ? Math.round(item.HoursWorked)
                : 0;
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                'TargetUtilisationRation'
              ] = Number(item.TargetUtilisationRation);
              hTotal +=
                roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                  'HoursWorked'
                ];
              roleRightData[item.EmployeeRole]['ActualFteEquivalentMTD'] =
                findRoleData
                  ? findRoleData.ActualFteEquivalentMTD
                  : 0;
            } else {
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                'ChargeableHoursWorkedMTD'
              ] += parseFloat(item.ChargeableHoursWorkedMTD);
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                'NonChargeableHoursWorkedMTD'
              ] += parseFloat(item.NonChargeableHoursWorkedMTD);
              const hoursToAdd = item.HoursWorked
                ? Math.round(item.HoursWorked)
                : 0;
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                'HoursWorked'
              ] += hoursToAdd;
              // Target % is the same each month; do not add when aggregating months
              roleRightData[item.EmployeeRole]['value'][item.EmployeeName][
                'TargetUtilisationRation'
              ] = Number(item.TargetUtilisationRation);
              hTotal += hoursToAdd;
            }
            avgHours.push(parseFloat(item.AverageHoursWorkedPerDay));
          }
        });
        Object.keys(roleRightData).map((idx) =>
          rightdata.push(roleRightData[idx]),
        );
      }
      rightdata.sort((a, b) => (a.Group > b.Group ? 1 : -1));
    }

    setAvailableTotal(avTotal);
    setRightdata(rightdata);
    setAvgHours(avgHours);
    setHoursTotal(hTotal);
    setGroupValidation(group_validation);
  }, [detailData, selectedLeftItems]);

  const deSelectAll = useCallback(() => {
    setSelectedLeftItems([]);
  }, []);

  const showTooltipHandler = useCallback((event, item, value) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let data = {},
      top,
      left;

    top = event.clientY - 70;
    left = event.clientX - 250;

    data = item;
    data['subitem'] = value;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      hideTooltip();
    }, 300);
  }, [hideTooltip]);

  const handleChart = useCallback((event, item, value) => {
    let _selectedRightItems, _selectedRightBars;

    if (event.shiftKey) {
      _selectedRightBars = selectedRightBars.slice();
      _selectedRightItems = selectedRightItems.slice();

      let index = NaN;
      for (let i = 0; i < selectedRightBars.length; i++) {
        if (selectedRightBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedRightBars.push(event.target);
        if (value === null) {
          // includes all names
          Object.keys(item.value).forEach((subd) => {
            _selectedRightItems.push({
              EmployeeRole: item.EmployeeRole,
              month: item.value[subd].month,
            });
          });
        } else {
          _selectedRightItems.push({
            EmployeeRole: item.EmployeeRole,
            month: value.month,
          });
        }
      } else {
        event.target.classList.remove('barActive');
        _selectedRightBars.splice(index, 1);
        _selectedRightItems.splice(index, 1);
      }
    } else {
      let exist = false;

      selectedRightBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedRightBars.length === 1) {
        _selectedRightItems = [];
        _selectedRightBars = [];
      } else {
        _selectedRightItems = [];
        if (value === null) {
          // includes all names
          Object.keys(item.value).forEach((subd) => {
            _selectedRightItems.push({
              EmployeeRole: item.EmployeeRole,
              EmployeeName: item.value[subd].EmployeeName,
            });
          });
        } else {
          _selectedRightItems.push({
            EmployeeRole: item.EmployeeRole,
            EmployeeName: value.EmployeeName,
          });
        }
        _selectedRightBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setSelectedRightBars(_selectedRightBars);
    setSelectedRightItems(_selectedRightItems);
  }, [selectedRightBars, selectedRightItems]);

  const getColor = useCallback((item, value, key = 0) => {
    let activeColor = item.Color[key];
    let disableColor = item.DisableColor[key];

    if (selectedRightItems.length === 0) {
      return activeColor;
    }

    for (let i = 0; i < selectedRightItems.length; i++) {
      if (
        selectedRightItems[i].EmployeeRole === item.EmployeeRole &&
        selectedRightItems[i].EmployeeName === value.EmployeeName
      ) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedRightItems]);

  const workingDays = getWorkingDaysInMonth(selectedDimDate);
  const avgHoursPerDay = availableTotal > 0 && workingDays > 0
    ? hoursTotal / (availableTotal * workingDays)
    : 0;
  const width = window.innerWidth - 13;
  const height = ((window.innerHeight - 100) * 2) / 3;
  const margin = {
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  };
  const { pageYOffset, xMax } = getParams(window, width, height, margin);

  const sanitizeValue = (value, defaultValue = 0) => {
    if (typeof value !== 'number' || !isFinite(value) || value < 0) {
      return defaultValue;
    }
    return value;
  };

  let rightWidth = sanitizeValue(xMax, 0);
  let offset = {};

  let rightGroupLengths = [],
    maxRightGroupLength = 0,
    rightRoleLengths = [],
    maxRightRoleLength = 0,
    rightNameLengths = [],
    maxRightNameLength = 0;

  if (Object.keys(detailData).length > 0) {
    rightdata.forEach((d) => {
      let groupDataLength = 0;
      d.subItems?.forEach(ds => {
        groupDataLength += ds.subItems.length;
      });

      if (groupDataLength === 1) {
        rightGroupLengths.push(d.Group?.length);
      } else {
        d.Group?.split(' ').forEach(word => {
          rightGroupLengths.push(word.length);
        });
      }
    });
    maxRightGroupLength = rightGroupLengths.length > 0 ? Math.max(...rightGroupLengths, 6) + 1: 7;

    detailData.Right.forEach((d) => {
      rightRoleLengths.push(d.EmployeeRole ? d.EmployeeRole.length : 0);
      rightNameLengths.push(d.EmployeeName ? d.EmployeeName.length : 0);
    });
    
    maxRightRoleLength = Math.max(...rightRoleLengths) + 1;
    maxRightNameLength = Math.max(...rightNameLengths);
  }

  const getBarWidth = () => {
    const width = groupValidation
      ? rightWidth -
        (maxRightGroupLength * 8) -
        (maxRightRoleLength + maxRightNameLength) * 6.6 -
        115
      : rightWidth - (maxRightRoleLength + maxRightNameLength) * 6.6 - 115;
    return sanitizeValue(width, 0);
  };

  const barWidth = getBarWidth();

  let rxScale = scaleLinear({
    domain: [0, 100],
    range: [0, barWidth],
    padding: 0.2,
    nice: true,
  });

  const getScaledValue = (value) => {
    const scaled = rxScale(value);
    return sanitizeValue(scaled, 0);
  };
  
  return (
    <div style={{ paddingTop: 8 }}>
      <Grid>
        <div className="subtitle mb-10">
          Utilisation (Avg. Hours Per Day:{' '}
          {Number(avgHoursPerDay || 0).toFixed(2)} )
        </div>

        {rightdata.length > 0 ? (
          <div
            className="flex"
            align="left"
            style={{
              borderBottom: 'solid 1px #cdcdcd',
              width: rightWidth,
            }}
          >
            {groupValidation ? (
              <div>
                <p style={formatedStyle(maxRightGroupLength * 8, 12, 0, 1)}>
                  Group
                </p>
              </div>
            ) : null}
            <div>
              <p style={formatedStyle(maxRightRoleLength * 6.6, 12, 0, 1)}>
                Role
              </p>
            </div>
            <div>
              <p style={formatedStyle(65, 12, 0, 1)}>Available</p>
            </div>
            <div>
              <p style={formatedStyle(maxRightNameLength * 6.6, 12, 0, 1)}>
                Name
              </p>
            </div>
            <div>
              <p style={formatedStyle(50, 12, 0, 1)}>Hours</p>
            </div>
          </div>
        ) : null}

        <div
          id="pr_bottom"
          style={{
            maxHeight: '41vh',
            overflowY: 'auto',
          }}
        >
          {rightdata.map((item, index) => {
            if (groupValidation) {
              let groupRenderItems = [],
                groupBorderLineWidth = 1;
              if (index === rightdata.length - 1) groupBorderLineWidth = 0;
              groupRenderItems = item.subItems?.map((gItem, gIndex) => {
                let roleRenderItems = [],
                  roleBorderLineWidth = 1;
                if (gIndex === item.subItems?.length - 1)
                  roleBorderLineWidth = 0;
                roleRenderItems = gItem.subItems?.map((rItem, rIndex) => {
                  let subItems = [],
                    renderItems = [],
                    borderLineWidth = 1;
                  if (rIndex === gItem.subItems?.length - 1)
                    borderLineWidth = 0;

                  Object.keys(rItem.value).forEach((subitem) => {
                    subItems.push(rItem.value[subitem]);
                    if (offset[rItem.value[subitem].month] === undefined)
                      offset[rItem.value[subitem].month] = 0;
                  });

                  subItems.forEach((value, ix) => {
                    let percent = 0;
                    let targetPercent = 0;
                    
                    if (value.HoursWorked && value.HoursWorked > 0) {
                      percent = Math.max(0, Math.min(100, 
                        (value.ChargeableHoursWorkedMTD / value.HoursWorked) * 100
                      ));
                    }

                    renderItems.push(
                      <div
                        key={`cateory2-${index}-${gIndex}-${rIndex}-${ix}`}
                        className="flex"
                        align="left"
                        style={{
                          alignItems: 'center',
                          borderBottom: `solid ${borderLineWidth}px #cdcdcd`,
                        }}
                      >
                        <div
                          style={formatedStyle(
                            maxRightNameLength * 6.6,
                            12,
                            0,
                            0,
                            'pointer',
                          )}
                        >
                          {value.EmployeeName}
                        </div>
                        <div
                          style={{
                            ...formatedStyle(50, 12, 0, 0, 'pointer'),
                            textAlign: 'center',
                          }}
                        >
                          {value.HoursWorked}
                        </div>
                        <div
                          style={formatedStyle(
                            barWidth,
                            12,
                            0,
                            0,
                            'pointer',
                          )}
                        >
                          <svg
                            height={20}
                            width={barWidth}
                            transform="translate(0, 1.5)"
                          >
                            <rect
                              x={0}
                              y={0}
                              height={20}
                              width={barWidth}
                              fill="transparent"
                              onClick={deSelectAll}
                            />
                            <Bar
                              x={0}
                              y={0}
                              width={getScaledValue(percent)}
                              height={20}
                              fill={getColor(rItem, value, 0)}
                              onClick={(event) =>
                                handleChart(event, rItem, value)
                              }
                              onMouseLeave={hideTooltipHandler}
                              onMouseMove={(event) =>
                                showTooltipHandler(event, rItem, value)
                              }
                              onTouchEnd={hideTooltipHandler}
                              onTouchMove={(event) =>
                                showTooltipHandler(event, rItem, value)
                              }
                            />
                            <Bar
                              x={getScaledValue(percent)}
                              y={0}
                              width={getScaledValue(100 - percent)}
                              height={20}
                              fill={getColor(rItem, value, 1)}
                              onClick={(event) =>
                                handleChart(event, rItem, value)
                              }
                              onMouseLeave={hideTooltipHandler}
                              onMouseMove={(event) =>
                                showTooltipHandler(event, rItem, value)
                              }
                              onTouchEnd={hideTooltipHandler}
                              onTouchMove={(event) =>
                                showTooltipHandler(event, rItem, value)
                              }
                            />
                            <Bar
                              x={getScaledValue(value.TargetUtilisationRation)}
                              y={-2}
                              width={0.5}
                              stroke="black"
                              strokeWidth={0.5}
                              height={24}
                              fill="black"
                              onMouseMove={(event) => {
                                showTooltipHandler(event, rItem, value);
                              }}
                              onMouseLeave={hideTooltipHandler}
                              onTouchMove={(event) => {
                                showTooltipHandler(event, rItem, value);
                              }}
                              onTouchEnd={hideTooltipHandler}
                            />
                          </svg>
                        </div>
                      </div>,
                    );
                  });

                  return renderItems;
                });

                return (
                  <div
                    key={`cateory2-${index}-${gIndex}`}
                    className="flex"
                    align="left"
                    style={{
                      borderBottom: `solid ${roleBorderLineWidth}px #cdcdcd`,
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    <div style={{ display: 'flex' }}>
                      <p
                        className="grayHover"
                        style={{
                          ...formatedStyle(
                            maxRightRoleLength * 6.6,
                            12,
                            0,
                            0,
                            'pointer',
                          ),
                          paddingTop: 4,
                        }}
                      >
                        {gItem.EmployeeRole}
                      </p>
                      <p
                        className="grayHover"
                        style={{
                          ...formatedStyle(65, 12, 0, 0, 'pointer'),
                          paddingTop: 4,
                          textAlign: 'center',
                        }}
                      >
                        {parseFloat(gItem.ActualFteEquivalentMTD).toFixed(1).includes('.0')
                          ? gItem.ActualFteEquivalentMTD
                          : parseFloat(gItem.ActualFteEquivalentMTD).toFixed(1)}
                      </p>
                    </div>
                    <div
                      style={{
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      {roleRenderItems}
                    </div>
                  </div>
                );
              });

              let rowCount = 0;
              item?.subItems?.forEach(
                (item) => (rowCount += item.subItems.length),
              );

              return (
                <div
                  key={`cateory2-${index}`}
                  className="flex"
                  align="left"
                  style={{
                    borderBottom: `solid ${groupBorderLineWidth}px #cdcdcd`,
                    padding: 0,
                    margin: 0,
                    width: rightWidth,
                  }}
                >
                  <div>
                    <p
                      className="grayHover"
                      style={{
                        width: maxRightGroupLength * 8,
                        fontSize: 12,
                        margin: 0,
                        cursor: 'pointer',
                        paddingTop: 4,
                      }}
                    >
                      {item.Group}
                    </p>
                  </div>
                  <div
                    style={{
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {groupRenderItems}
                  </div>
                </div>
              );
            } else {
              let subItems = [],
                renderItems = [],
                borderLineWidth = 1;
              if (index === rightdata.length - 1) borderLineWidth = 0;

              Object.keys(item.value).forEach((subitem) => {
                subItems.push(item.value[subitem]);
                if (offset[item.value[subitem].month] === undefined)
                  offset[item.value[subitem].month] = 0;
              });

              subItems.forEach((value, rIndex) => {
                let percent = 0;
                
                if (value.HoursWorked && value.HoursWorked > 0) {
                  percent = Math.max(0, Math.min(100,
                    (value.ChargeableHoursWorkedMTD / value.HoursWorked) * 100
                  ));
                }
                renderItems.push(
                  <div key={`cateory2-${index}-${rIndex}`}>
                    <div
                      className="flex"
                      align="left"
                      style={{ alignItems: 'center' }}
                    >
                      <div
                        style={formatedStyle(
                          maxRightNameLength * 6.6,
                          12,
                          0,
                          0,
                          'pointer',
                        )}
                      >
                        {value.EmployeeName}
                      </div>
                      <div
                        style={{
                          ...formatedStyle(50, 12, 0, 0, 'pointer'),
                          textAlign: 'center',
                        }}
                      >
                        {value.HoursWorked}
                      </div>
                      <div
                        style={formatedStyle(
                          barWidth,
                          12,
                          0,
                          0,
                          'pointer',
                        )}
                      >
                        <svg
                          height={20}
                          width={barWidth}
                          transform="translate(0, 1.5)"
                        >
                          <rect
                            x={0}
                            y={0}
                            height={20}
                            width={barWidth}
                            fill="transparent"
                            onClick={deSelectAll}
                          />
                          <Bar
                            x={0}
                            y={0}
                            width={getScaledValue(percent)}
                            height={20}
                            fill={getColor(item, value, 0)}
                            onClick={(event) =>
                              handleChart(event, item, value)
                            }
                            onMouseLeave={hideTooltipHandler}
                            onMouseMove={(event) =>
                              showTooltipHandler(event, item, value)
                            }
                            onTouchEnd={hideTooltipHandler}
                            onTouchMove={(event) =>
                              showTooltipHandler(event, item, value)
                            }
                          ></Bar>
                          <Bar
                            x={getScaledValue(percent)}
                            y={0}
                            width={getScaledValue(100 - percent)}
                            height={20}
                            fill={getColor(item, value, 1)}
                            onClick={(event) =>
                              handleChart(event, item, value)
                            }
                            onMouseLeave={hideTooltipHandler}
                            onMouseMove={(event) =>
                              showTooltipHandler(event, item, value)
                            }
                            onTouchEnd={hideTooltipHandler}
                            onTouchMove={(event) =>
                              showTooltipHandler(event, item, value)
                            }
                          ></Bar>
                        </svg>
                      </div>
                    </div>
                  </div>,
                );
              });

              return (
                <div
                  key={`cateory2-${index}`}
                  className="flex"
                  align="left"
                  style={{
                    borderBottom: `solid ${borderLineWidth}px #cdcdcd`,
                    padding: 0,
                    margin: 0,
                    width: rightWidth,
                  }}
                >
                  <div style={{ display: 'flex' }}>
                    <p
                      className="grayHover"
                      style={{
                        ...formatedStyle(
                          maxRightRoleLength * 6.6,
                          12,
                          0,
                          0,
                          'pointer',
                        ),
                        paddingTop: 4,
                      }}
                    >
                      {item.EmployeeRole}
                    </p>
                    <p
                      className="grayHover"
                      style={{
                        ...formatedStyle(65, 12, 0, 0, 'pointer'),
                        paddingTop: 4,
                        textAlign: 'center',
                      }}
                    >
                      {parseFloat(item.ActualFteEquivalentMTD).toFixed(1).includes('.0')
                          ? item.ActualFteEquivalentMTD
                          : parseFloat(item.ActualFteEquivalentMTD).toFixed(1)}
                    </p>
                  </div>
                  <div
                    style={{
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {renderItems}
                  </div>
                </div>
              );
            }
          })}

          {/* Total Line */}
          <div
            className="flex"
            align="left"
            style={{
              borderTop: 'solid 1px #cdcdcd',
              borderBottom: 'solid 1px #cdcdcd',
              padding: 0,
              margin: 0,
              width: rightWidth,
            }}
          >
            {groupValidation ? (
              <div
                style={{
                  width: maxRightGroupLength * 8,
                }}
              />
            ) : null}
            <div
              className="flex"
              align="left"
              style={{
                padding: 0,
                margin: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                }}
              >
                <p
                  style={{
                    ...formatedStyle(
                      maxRightRoleLength * 6.6,
                      12,
                      0,
                      0,
                      'pointer',
                    ),
                    paddingTop: 4,
                    fontWeight: 'bold',
                  }}
                >
                  Total
                </p>
                <p
                  style={{
                    ...formatedStyle(65, 12, 0, 0, 'pointer'),
                    paddingTop: 4,
                    textAlign: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  {parseFloat(availableTotal).toFixed(1)}
                </p>
              </div>
              <div
                style={{
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                }}
              >
                <div
                  style={formatedStyle(
                    maxRightNameLength * 6.6,
                    12,
                    0,
                    0,
                    'pointer',
                  )}
                />
                <p
                  style={{
                    ...formatedStyle(50, 12, 0, 0, 'pointer'),
                    paddingTop: 4,
                    textAlign: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  {thousandFormat(hoursTotal)}
                </p>
                <div
                  style={formatedStyle(
                    barWidth,
                    12,
                    0,
                    0,
                    'pointer',
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        
        {rightdata.length > 0 ? (
          <div className="flex" style={{ position: 'relative' }}>
            <svg height={20} width={sanitizeValue(rightWidth + 17, 17)}>
              <rect
                x={0}
                y={0}
                width={sanitizeValue(rightWidth + 2, 2)}
                height={30}
                fill="transparent"
              />
              <AxisBottom
                hideTicks={false}
                numTicks={5}
                scale={rxScale}
                top={0}
                left={sanitizeValue(
                  groupValidation
                    ? maxRightGroupLength * 8 +
                      (maxRightRoleLength +
                        maxRightNameLength) *
                        6.6 +
                      115
                    : (maxRightRoleLength + maxRightNameLength) * 6.6 + 115,
                  0
                )}
                stroke="black"
                tickStroke="black"
                tickLabelProps={(value, index) => ({
                  fontSize: 11,
                  textAnchor: 'middle',
                  dy: '-0.1em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text {...tickProps} fill="black">
                    {formattedValue}%
                  </text>
                )}
              />
            </svg>
          </div>
        ) : null}
      </Grid>

      {tooltipOpen && (
        <TooltipWithBounds
         
          top={tooltipTop + pageYOffset}
          left={tooltipLeft}
        >
          <div className="bkg-white color-black">
            <div className="pdv-10">
              <strong> {tooltipData['subitem'].EmployeeName} </strong>
            </div>
            <div className="ft-14">
              Role:&nbsp;&nbsp;&nbsp;&nbsp;
              <strong>{tooltipData.EmployeeRole} </strong>
            </div>
            <div className="ft-14">
              Chargeable Hours:&nbsp;
              <strong>
                {thousandFormat(
                  parseFloat(
                    tooltipData['subitem'].ChargeableHoursWorkedMTD,
                  ).toFixed(1),
                )}{' '}
              </strong>
            </div>
            <div className="ft-14">
              Non-Chargeable:&nbsp;&nbsp;&nbsp;&nbsp;
              <strong>
                {Number(
                  tooltipData['subitem'].NonChargeableHoursWorkedMTD,
                ).toFixed(1)}{' '}
              </strong>
            </div>
            <div className="ft-14">
              Hours Worked:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong>
                {thousandFormat(
                  parseFloat(tooltipData['subitem'].HoursWorked).toFixed(1),
                )}{' '}
              </strong>
            </div>
            <div className="ft-14">&nbsp;&nbsp;</div>
            <div className="ft-14">
              Target Utilisation:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong>
                {thousandFormat(
                  parseFloat(tooltipData['subitem'].TargetUtilisationRation).toFixed(1),
                )}%
              </strong>
            </div>
            <div className="ft-14">
              Percent Chargeable:{' '}
              <strong>
                {Math.round(
                  (tooltipData['subitem'].ChargeableHoursWorkedMTD /
                    tooltipData['subitem'].HoursWorked) *
                    100,
                )}%
              </strong>
            </div>
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};

BottomChart.propTypes = {
  detailData: PropTypes.object.isRequired,
  selectedDimDate: PropTypes.string.isRequired,
};

export default BottomChart;
